import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import BlogCard from './BlogCard';
import { getPublishedBlogPosts } from '../data/blogData';
import { getTranslatedBlogContent } from '../data/blogTranslations';

const BlogList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const rawPosts = getPublishedBlogPosts();
  const posts = rawPosts.map(post => ({
    ...post,
    title: getTranslatedBlogContent(post.slug || '', i18n.language)?.title || post.title,
    excerpt: getTranslatedBlogContent(post.slug || '', i18n.language)?.excerpt || post.excerpt
  }));

  return (
    <Container>
      <Header>
        <Title
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {t('blog.latestArticles')}
        </Title>
        <Subtitle
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {t('blog.stayInformed')}
        </Subtitle>
      </Header>

      <Grid
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        {posts.map((post, index) => (
          <GridItem
            key={post.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <BlogCard post={post} />
          </GridItem>
        ))}
      </Grid>
    </Container>
  );
};

const Container = styled.div`
  max-width: var(--container-xl);
  margin: 0 auto;
  padding: var(--spacing-xxl) var(--spacing-lg);

  @media (max-width: 768px) {
    padding: var(--spacing-xl) var(--spacing-md);
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-xxl);
`;

// h2, not h1: BlogListPage already renders a PageHeader h1 above this, and two
// h1s on one page split the topical signal.
const Title = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: var(--spacing-md);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.1rem;
  color: var(--gray);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Grid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  grid-auto-rows: 1fr;
  align-items: stretch;
  gap: var(--spacing-xl);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }
`;

const GridItem = styled(motion.div)`
  display: flex;
  width: 100%;
  height: 100%;
`;

export default BlogList;
